/**
 * Turns a customer's square footage into a whole number of boxes, then shows what those boxes cost.
 *
 * Boxes are sold whole. 25 sqft plus 15% extra is about 28.75 sqft. At 9.77 sqft per box that is
 * 3 boxes, and 3 × the variant price ($371.26) is $1,113.78. That dollar amount is the same number
 * checkout charges. The hidden form quantity is the box count, not the square footage.
 *
 * effective = enteredSqft × (1 + overage% / 100)
 * boxes = max(1, ceil(effective / sqFtPerBox))
 * totalSqft = boxes × sqFtPerBox
 * orderCents = boxes × variant price in cents
 *
 * See docs/dev/docs-box-quantity-calculator.md
 */
if (!customElements.get('box-quantity-calculator')) {
  customElements.define(
    'box-quantity-calculator',
    class BoxQuantityCalculator extends HTMLElement {
      connectedCallback() {
        this.sqftInput = this.querySelector('[data-box-sqft]')
        this.overageSelect = this.querySelector('[data-overage-select]')
        this.boxCountEl = this.querySelector('[data-box-count]')
        this.totalEl = this.querySelector('[data-box-total]')
        this.qtyInput = this.querySelector('.quantity__input')
        this.missingMessage = this.querySelector('[data-box-missing-message]')
        this.atcButton = document.querySelector('.product-form__submit')
        this.variantIdInput =
          this.closest('product-info')?.querySelector('.product-variant-id') ||
          document.querySelector(
            `#product-form-${this.dataset.sectionId} [name="id"]`
          ) ||
          document.querySelector('[name="id"]')

        this.minSqft = parseFloat(this.dataset.minSqft) || 25
        this.maxSqft = parseFloat(this.dataset.maxSqft) || 400
        this.coverageByVariant = this.readCoverage()

        this.onInput = this.onInput.bind(this)
        this.onVariantChange = this.onVariantChange.bind(this)

        this.sqftInput?.addEventListener('input', this.onInput)
        this.sqftInput?.addEventListener('change', this.onInput)
        this.addEventListener('overage:change', this.onInput)

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange
          )
        }

        // The per-box price is replaced when the size changes. Recalculate then,
        // so Total uses the new box price rather than the previous size.
        const priceRoot = this.closest('product-info')?.querySelector(
          '[id^="price-"]'
        )
        if (priceRoot) {
          this.priceObserver = new MutationObserver(() => this.recalculate())
          this.priceObserver.observe(priceRoot, {
            childList: true,
            subtree: true
          })
        }

        this.recalculate()
      }

      disconnectedCallback() {
        this.sqftInput?.removeEventListener('input', this.onInput)
        this.sqftInput?.removeEventListener('change', this.onInput)
        this.removeEventListener('overage:change', this.onInput)
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber()
        this.priceObserver?.disconnect()
      }

      readCoverage() {
        const script = this.querySelector('[data-box-coverage]')
        if (!script) return {}
        try {
          return JSON.parse(script.textContent.trim())
        } catch (e) {
          console.error('box-quantity-calculator: invalid coverage JSON', e)
          return {}
        }
      }

      currentVariantId() {
        const id = this.variantIdInput?.value
        return id ? String(id) : null
      }

      sqFtPerBox() {
        const id = this.currentVariantId()
        if (!id) return null
        const value = this.coverageByVariant[id]
        if (value == null || value === '') return null
        const num = parseFloat(value)
        return Number.isFinite(num) && num > 0 ? num : null
      }

      overagePercent() {
        const value = parseFloat(this.overageSelect?.value)
        return Number.isFinite(value) && value > 0 ? value : 0
      }

      onVariantChange(event) {
        const sectionId = this.dataset.sectionId
        if (event?.data?.sectionId && event.data.sectionId !== sectionId) return
        // Variant id input may update after publish; wait a tick.
        requestAnimationFrame(() => this.recalculate())
      }

      onInput() {
        this.recalculate()
      }

      /**
       * Recalculates boxes, covered square footage, and the order total from the field.
       * Outside the min/max, or when this size has no coverage, every figure becomes an em dash
       * and Add to Cart is disabled. Overage is applied before the box count, and the min/max
       * check uses the number the customer typed, before that extra.
       */
      recalculate() {
        const sqFtPerBox = this.sqFtPerBox()
        const entered = parseFloat(this.sqftInput?.value)

        if (!sqFtPerBox) {
          this.setDisplay('—', '—')
          this.setQuantity(0)
          this.writeOrderTotal(0)
          this.toggleMissing(true)
          this.setAtcEnabled(false)
          return
        }

        this.toggleMissing(false)

        if (
          !Number.isFinite(entered) ||
          entered < this.minSqft ||
          entered > this.maxSqft
        ) {
          this.setDisplay('—', '—')
          this.setQuantity(0)
          this.writeOrderTotal(0)
          this.setAtcEnabled(false)
          return
        }

        // Need 28.75 sqft and each box covers 9.77, so ceil(28.75 / 9.77) is 3 boxes.
        const effective = entered * (1 + this.overagePercent() / 100)
        const boxes = Math.max(1, Math.ceil(effective / sqFtPerBox))
        // What those whole boxes actually cover, not the number the customer typed.
        const total = boxes * sqFtPerBox

        this.setDisplay(String(boxes), total.toFixed(this.coverageDecimals(sqFtPerBox)))
        this.setQuantity(boxes)
        this.writeOrderTotal(boxes)
        this.setAtcEnabled(true)
      }

      /**
       * 9.77 needs two decimals, so 3 boxes show 29.31. 7.7 stays one decimal, so 2 boxes show 15.4.
       */
      coverageDecimals(sqFtPerBox) {
        const hundredths = Math.round(sqFtPerBox * 100)
        return hundredths % 10 === 0 ? 1 : 2
      }

      /**
       * Turns a cent total into the dollar string Shopify would charge, cents included.
       * 111378 cents is $1,113.78. Rounding to the nearest cent only guards against
       * a fractional product. It does not drop or raise the price to a whole dollar.
       */
      formatOrderCents(totalCents) {
        const cents = Math.round(totalCents)
        const sign = cents < 0 ? '-' : ''
        const abs = Math.abs(cents)
        const dollars = Math.floor(abs / 100)
        const rem = String(abs % 100).padStart(2, '0')
        return `${sign}$${dollars.toLocaleString('en-US')}.${rem}`
      }

      /**
       * Writes boxes × the variant price into the Total row above the buy buttons.
       * data-price on [data-box-unit-price] is one box in cents, and it updates when
       * the size changes because that node lives in the price block. 3 × 37126 =
       * 111378 cents, shown as $1,113.78. Checkout multiplies that same variant price
       * by the hidden box quantity.
       */
      writeOrderTotal(boxes) {
        const root = this.closest('product-info') || document
        const totalEl = root.querySelector('.product__order-total [data-order-total]')
        if (!totalEl) return
        const unitCents = parseFloat(
          root.querySelector('[data-box-unit-price]')?.getAttribute('data-price')
        )
        if (!boxes || !Number.isFinite(unitCents)) {
          totalEl.textContent = '—'
          return
        }
        totalEl.textContent = this.formatOrderCents(unitCents * boxes)
      }

      setDisplay(boxesText, totalText) {
        if (this.boxCountEl) this.boxCountEl.textContent = boxesText
        if (this.totalEl) this.totalEl.textContent = totalText
      }

      setQuantity(boxes) {
        if (!this.qtyInput) return
        this.qtyInput.value = String(boxes)
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }))
      }

      toggleMissing(missing) {
        this.missingMessage?.classList.toggle('hidden', !missing)
      }

      setAtcEnabled(enabled) {
        if (!this.atcButton) return
        if (enabled) {
          this.atcButton.removeAttribute('disabled')
        } else {
          this.atcButton.setAttribute('disabled', 'true')
        }
      }
    }
  )
}
